import { Component, h, Host, Prop, State, Watch, Element } from '@stencil/core';
import { Consumes } from '@sydle/sydle-ui';
import { getBaseURL } from '../../../env/enviroment';
import { makeContextAvailability } from '../../utils/contextsAvailability';
import { getAuthState } from '../sy-site-auth/store/store';
import { Quiz, SubmitQuizAnswer } from './model';

@Component({
  tag: 'sy-growth-quiz-bootstrap',
  styleUrl: 'sy-growth-quiz-bootstrap.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SySiteQuiz {
  @Element() el: HTMLSyGrowthQuizBootstrapElement;
  private startButton: HTMLSyButtonElement;
  private submitButton: HTMLSyButtonElement;

  private contextsAvailability = makeContextAvailability(['api', 'auth']);

  @Consumes('siteApi') @State() api: HTMLSySiteApiElement;
  @Consumes('siteAuth') @State() auth: HTMLSySiteAuthElement;

  @State() isQuizStarted = false;
  @State() questionIndex = 0;
  @State() answerId: string;
  @State() isQuizFinished = false;
  @State() quizResult: SubmitQuizAnswer;
  @State() quiz: Quiz;
  @State() isLastQuestion = false;
  @State() answersButtonsDisabled = false;
  @State() markedAnswers = [];
  @State() shadowBody: HTMLDivElement;

  /**
   * Id of the quiz to be displayed
   */
  @Prop() quizObj: string;

  @Watch('api')
  @Watch('auth')
  setContexts(): void {
    if (this.api) {
      this.contextsAvailability.props.api.resolve();
    }

    if (this.auth) {
      this.contextsAvailability.props.auth.resolve();
    }
  }

  async componentWillLoad() {
    this.setContexts();
    await this.contextsAvailability.all;
    await this.auth.whenReady();
    if (getAuthState('isAuthenticated')) {
      if (localStorage.getItem('quizAnswerId')) {
        this.answerId = localStorage.getItem('quizAnswerId');
        localStorage.removeItem('quizAnswerId');
        this.isQuizStarted = true;
        const result = await this.api.submitQuizAnswer(this.answerId);
        this.quizResult = result;
        this.isQuizFinished = true;
      }
    }
    return this.api.loadQuizData(this.quizObj).then(quiz => (this.quiz = quiz));
  }

  // Função para criar uma resposta do usuário para o quiz
  private async createAnswer() {
    if (!this.startButton.disabled) {
      this.startButton.disabled = true;
      this.startButton.label = '';
      this.startButton.loading = true;
      this.api.createQuizAnswer(this.quizObj).then(answer => {
        this.answerId = answer._id;
        this.isQuizStarted = true;
      });
    }
  }

  // Função para responder uma questão
  private answerQuestion(alternative, questionIndex, button) {
    if (!button.disabled) {
      this.answersButtonsDisabled = true;
      this.markedAnswers[questionIndex] = alternative;
      this.api
        .answerQuizQuestion(this.answerId, this.quiz.quizForm.quizAnswerAndQuestion[this.questionIndex].possibleAnswers.indexOf(alternative), questionIndex)
        .then(_output => _output.nextQuestionIndex)
        .then(nextQuestionIndex => {
          if (nextQuestionIndex !== null) {
            this.questionIndex = nextQuestionIndex;
            this.answersButtonsDisabled = false;
          } else {
            this.isLastQuestion = true;
            this.answersButtonsDisabled = false;
          }
        });
    }
  }

  //Função para submeter as respostas ao quiz
  private async submitQuiz() {
    if (!this.submitButton.disabled) {
      this.submitButton.disabled = true;
      this.submitButton.label = null;
      localStorage.setItem('quizAnswerId', this.answerId);
      if (getAuthState('user')) {
        await this.api.submitQuizAnswer(this.answerId).then(result => {
          this.quizResult = result;
          this.isQuizFinished = true;
        });
      } else {
        this.auth.whenReady().then(() => this.auth.login());
      }
    }
  }

  // Passa a página
  private nextQuestion() {
    this.questionIndex = this.questionIndex + 1;
  }
  //Retona a página
  private previousQuestion() {
    this.questionIndex = this.questionIndex - 1;
  }

  private getBackground1Styles() {
    const baseUrl = getBaseURL();
    const url = `url(${baseUrl}com.sydle.growth.marketing/quizApi/assets/${this.quiz._id}/horizontalBackgroundImage)`;
    const url2 = `url(${baseUrl}com.sydle.growth.marketing/quizApi/assets/${this.quiz._id}/verticalBackgroundImage)`;

    if (this.isQuizStarted) {
      return {
        'color': 'black',
        '--background-color': 'var(--sy-surface-light)',
      };
    }

    return {
      '--background-image-horizontal-url': url,
      '--background-image-vertical-url': url2,
      'color': 'white',
    };
  }

  private getBackground2Styles() {
    if (this.isQuizStarted) {
      return null;
    }

    return {
      background: 'linear-gradient(180deg, rgba(186, 209, 237, 0.4503) 0%, rgba(0, 0, 0, 0.57) 41.46%)',
    };
  }

  // Página Inicial
  private initialPage() {
    return (
      <div class="flex-align cover">
        <sy-text type="h2">Quiz</sy-text>
        <div class="flex-align  inner-content">
          <sy-text type="h3">{this.quiz.name}</sy-text>
          {this.quiz.description && <sy-text type="body">{this.quiz.description}</sy-text>}
        </div>
        <sy-button ref={el => (this.startButton = el)} label="Responda agora" type="accent" button-type="button" onClick={() => this.createAnswer()}></sy-button>
      </div>
    );
  }

  private getPaginator() {
    const isLastIconVisible = this.quiz.quizForm.quizAnswerAndQuestion.length === this.questionIndex + 1;
    return (
      <div class="paginator">
        <div class="inner">
          {this.questionIndex === 0 ? null : (
            <sy-icon name="arrow-left-1" path="assets/icons" family="streamline-light" role="img" color="accent" onClick={() => this.previousQuestion()}></sy-icon>
          )}
          <sy-text type="body">
            {this.questionIndex + 1}/{this.quiz.quizForm.quizAnswerAndQuestion.length}
          </sy-text>
          <sy-icon
            style={{ visibility: isLastIconVisible ? 'hidden' : 'inherit' }}
            name="arrow-right-1"
            path="assets/icons"
            family="streamline-light"
            color="accent"
            role="img"
            onClick={() => this.nextQuestion()}
          ></sy-icon>
        </div>
      </div>
    );
  }

  // Formulário do Quiz
  private quizForm() {
    if (this.isLastQuestion) {
      return (
        <div class="flex-align quiz-answered" style={{ width: '100%', height: '100%' }}>
          <sy-text type="h2" style={{ color: 'var(--sy-accent)' }}>
            Quiz Respondido
          </sy-text>
          <sy-button ref={el => (this.submitButton = el)} label="Enviar resposta" type="accent" button-type="button" onClick={() => this.submitQuiz()} />
        </div>
      );
    }
    return (
      <div id="quiz-form" class="flex-align" style={{ height: '100%', gap: '24px' }}>
        <div class="toolbar">
          <sy-text type="h2" style={{ color: 'var(--sy-accent)' }}>
            Quiz
          </sy-text>
          {this.getPaginator()}
        </div>
        <div class="shadow-body" ref={el => (this.shadowBody = el)}>
          <div class="shadow top"></div>
          <div class="shadow bottom"></div>
          <div id="question-body">
            <sy-text type="body">{this.questionIndex === null ? null : this.quiz.quizForm.quizAnswerAndQuestion[this.questionIndex].question}</sy-text>
          </div>
        </div>
        <div class="flex-align anwser-buttons">
          {this.quiz.quizForm.quizAnswerAndQuestion[this.questionIndex].possibleAnswers.map(alternative => {
            return (
              <sy-button
                key={alternative}
                disabled={this.answersButtonsDisabled ? true : undefined}
                content="icon-left"
                type={this.markedAnswers[this.questionIndex] && this.markedAnswers[this.questionIndex] === alternative ? 'accent' : 'default'}
                button-type="button"
                onClick={e => this.answerQuestion(alternative, this.questionIndex, e.target)}
                label={alternative}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Página de resultados
  private getSubmit() {
    return (
      <div class="flex-align quiz-answer">
        <sy-text type="h2" style={{ color: 'var(--sy-accent)' }}>
          Quiz
        </sy-text>
        <sy-text type="body" style={{ 'align-self': 'flex-start', 'margin-top': '32px' }}>
          <b>
            Você acertou {this.quizResult.totalOfRightAnswers} das {this.quiz.quizForm.quizAnswerAndQuestion.length} questões!
          </b>
        </sy-text>

        <sy-text type="body" class="final-text">
          {'\n' + this.quiz.finalQuizText}
        </sy-text>
      </div>
    );
  }

  @Watch('shadowBody')
  shadowBodyWatcher(): void {
    if (this.shadowBody) {
      const promise = new Promise<HTMLDivElement>(resolve => {
        const observer = new MutationObserver(() => {
          resolve(this.shadowBody);
        });
        observer.observe(this.shadowBody, {
          childList: true,
        });
      });

      promise.then(shadowBody => {
        const shadowTop = shadowBody.querySelector('.shadow.top') as HTMLDivElement;
        const shadowBottom = shadowBody.querySelector('.shadow.bottom') as HTMLDivElement;
        const questionBody = shadowBody.querySelector('#question-body') as HTMLDivElement;

        const setShadows = () => {
          const contentScrollHeight = questionBody.scrollHeight - shadowBody.offsetHeight;
          const currentScroll = questionBody.scrollTop / contentScrollHeight;

          if (questionBody.scrollHeight > shadowBody.offsetHeight) {
            shadowTop.style.opacity = currentScroll.toString();
            shadowBottom.style.opacity = (1 - currentScroll).toString();
          }
        };

        const questionBodyObserver = new MutationObserver(() => {
          shadowTop.style.opacity = '0';
          shadowBottom.style.opacity = '0';
          setShadows();
        });
        questionBodyObserver.observe(questionBody, { childList: true, subtree: true, characterData: true });

        questionBody.addEventListener('scroll', setShadows);
      });
    }
  }

  componentDidLoad() {
    this.shadowBodyWatcher();
    if (getAuthState('isAuthenticated') && this.quizResult) {
      this.el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  render() {
    return (
      <Host>
        <div class="background-1" style={this.getBackground1Styles()}>
          <div class="background-2" style={this.getBackground2Styles()}>
            {this.isQuizFinished ? this.getSubmit() : this.isQuizStarted ? this.quizForm() : this.initialPage()}
          </div>
        </div>
      </Host>
    );
  }
}
